import {useEffect, useState, type ReactNode} from "react";
import { AlertTriangle, Check, Clock3, Minus, X } from "lucide-react";
import type { ExportJobStatus, JobStatus, RuleStatus, SimulationStatus } from "@signum/api-contracts";

export function StatusBadge({status}: {status: JobStatus | ExportJobStatus | RuleStatus | SimulationStatus | "RECOMMENDED_BUY" | "NO_BUY" | "SUCCESS" | "ACTIVE" | "INACTIVE" | "UNVERIFIED"}) {
  const kind = ["PASS", "COMPLETE", "READY", "RECOMMENDED_BUY", "SUCCESS", "ACTIVE"].includes(status) ? "positive"
    : ["FAIL", "FAILED", "NO_BUY", "CANCELLED", "INACTIVE"].includes(status) ? "negative"
    : ["MISSING_DATA", "PARTIAL", "UNVERIFIED"].includes(status) ? "warning" : "neutral";
  const Icon = kind === "positive" ? Check : kind === "negative" ? X : kind === "warning" ? AlertTriangle : Clock3;
  return <span className={`status ${kind}`}><Icon size={12}/>{label(status)}</span>;
}

export function Score({value, max = 7}: {value: number; max?: number}) {
  return <span className="score" aria-label={`${value} of ${max} points`}>{Array.from({length: max}, (_, index) =>
    <span key={index} className={index < value ? "earned" : ""}/>) }<strong>{value}/{max}</strong></span>;
}

export function Empty({title, children}: {title: string; children?: ReactNode}) {
  return <div className="empty"><Minus size={22}/><strong>{title}</strong>{children && <p>{children}</p>}</div>;
}

export function Loading() { return <div className="loading"><span/><span/><span/></div>; }
export function ErrorState({error}: {error: unknown}) { return <div className="error-panel"><AlertTriangle size={20}/><div><strong>Unable to load this view</strong><p>{error instanceof Error ? error.message : String(error)}</p></div></div>; }
export function Money({value}: {value: string | number}) { return <>{new Intl.NumberFormat("en-US", {style: "currency", currency: "USD", maximumFractionDigits: 0}).format(Number(value))}</>; }
export function NumberValue({value}: {value: string | number}) { return <>{new Intl.NumberFormat("en-US", {maximumFractionDigits: 3}).format(Number(value))}</>; }
export function DateTime({value, dateOnly = false}: {value?: string; dateOnly?: boolean}) {
  if (!value) return <>—</>;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return <>{new Intl.DateTimeFormat("en-US", dateOnly ? {month: "short", day: "numeric", year: "numeric"} :
    {month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit"}).format(date)}</>;
}
export function RelativeTime({value}: {value?: string}) {
  if (!value) return <>—</>;
  const minutes = Math.round((new Date(value).valueOf() - Date.now()) / 60_000);
  if (minutes === 0) return <>just now</>;
  if (Math.abs(minutes) < 60) return <>{minutes >= 0 ? "in " : ""}{Math.abs(minutes)} min{minutes < 0 ? " ago" : ""}</>;
  const hours = Math.round(minutes / 60); return <>{hours >= 0 ? "in " : ""}{Math.abs(hours)} hr{Math.abs(hours) !== 1 ? "s" : ""}{hours < 0 ? " ago" : ""}</>;
}
export function ElapsedTime({start, end}: {start?: string; end?: string}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { if (!start || end) return; const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer); }, [start, end]);
  if (!start) return <>Waiting to start</>;
  const milliseconds = Math.max(0, (end ? new Date(end).valueOf() : now) - new Date(start).valueOf());
  const seconds = Math.floor(milliseconds / 1_000); const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor(seconds % 3_600 / 60); const remainder = seconds % 60;
  return <>{hours ? `${hours}:` : ""}{String(minutes).padStart(hours ? 2 : 1, "0")}:{String(remainder).padStart(2, "0")}</>;
}
export function JsonBlock({value}: {value: unknown}) { return <pre className="json-block">{JSON.stringify(value, null, 2)}</pre>; }
function label(value: string) { return value.toLowerCase().replaceAll("_", " ").replace(/^./, (character) => character.toUpperCase()); }
